import { useMemo, useState } from 'react';
import { Contact, Download, Loader2, Search, Trash2, BanIcon, Star, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsPrivileged } from '@/hooks/useAuth';
import { CATEGORY_LABEL } from '@/modules/sales-research/lib';
import type { CompanyCategory } from '@/modules/sales-research/types';
import { useContacts, useUnsubCount, useCompanyCount, useRunEnrich, useCrmAction, useCancelJob, type ContactRow } from './api';
import { Select } from './widgets';

const ROLES = ['hr', 'people_ops', 'office_manager', 'facilities', 'admin', 'procurement', 'founder', 'other'];

export function ContactsTab() {
  const contactsQ = useContacts();
  const unsubQ = useUnsubCount();
  const companyCountQ = useCompanyCount();
  const enrich = useRunEnrich();
  const cancelJob = useCancelJob();
  const privileged = useIsPrivileged();
  const act = useCrmAction();

  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [source, setSource] = useState('');
  const [emailOnly, setEmailOnly] = useState(false);

  const all = contactsQ.data ?? [];
  const rows = useMemo(() => {
    let r = all;
    if (role) r = r.filter((c) => c.role_category === role);
    if (source) r = r.filter((c) => c.source === source);
    if (emailOnly) r = r.filter((c) => !!c.email);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((c) =>
        (c.full_name ?? '').toLowerCase().includes(s) ||
        (c.email ?? '').toLowerCase().includes(s) ||
        c.company_name.toLowerCase().includes(s));
    }
    return r;
  }, [all, role, source, emailOnly, q]);

  const withEmail = all.filter((c) => c.email).length;
  const covered = new Set(all.map((c) => c.company_id)).size;
  const totalCompanies = companyCountQ.data ?? 0;
  const remaining = Math.max(totalCompanies - covered, 0);

  function exportCsv() {
    const cols = ['full_name', 'title', 'role_category', 'email', 'phone', 'company_name', 'company_city', 'company_category', 'source', 'assigned_name', 'is_suppressed'];
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [cols.join(','), ...rows.map((c) => cols.map((k) => esc((c as any)[k])).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `flax-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="space-y-3">
      {/* stats + actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Stat label="Companies covered" value={`${covered}/${totalCompanies}`} sub={remaining > 0 ? `${remaining} to enrich` : 'all covered'} />
        <Stat label="Contacts" value={all.length} />
        <Stat label="With email" value={withEmail} />
        <Stat label="Unsubscribed" value={unsubQ.data ?? 0} tone="magenta" />
        <div className="ml-auto flex items-center gap-2">
          {privileged ? (
            <>
              {enrich.isPending && (
                <Button size="sm" variant="destructive" onClick={() => cancelJob.mutate('enrich')} disabled={cancelJob.isPending}>
                  <Square className="h-3.5 w-3.5" /> Stop
                </Button>
              )}
              <Button size="sm" variant="outline" disabled={enrich.isPending}
                onClick={() => enrich.mutate({ limit: 25 }, { onSuccess: () => contactsQ.refetch() })}>
                {enrich.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Contact className="h-4 w-4" />}
                Find contacts
              </Button>
            </>
          ) : (
            <span className="text-2xs text-muted-foreground">Find contacts is run by Gurmeet.</span>
          )}
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={rows.length === 0}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {enrich.data && (
        <p className="text-2xs text-muted-foreground">
          Found {enrich.data.contacts_created ?? 0} contacts across {enrich.data.companies_touched ?? 0} companies
          {enrich.data.deadline_hit ? ' — run again for more.' : '.'} Sites built as apps may hide emails; add those by hand.
        </p>
      )}

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…"
            className="h-9 w-64 rounded-md border border-border/60 bg-transparent pl-7 pr-2 text-sm outline-none focus:border-[#51BFAA]" />
        </div>
        <Select value={role} onChange={setRole} placeholder="All roles"
          options={ROLES.map((r) => ({ value: r, label: r.replace('_', ' ') }))} />
        <Select value={source} onChange={setSource} placeholder="All sources"
          options={[
            { value: 'apollo', label: 'Apollo (named)' },
            { value: 'lusha', label: 'Lusha (named)' },
            { value: 'enrichment', label: 'Website email' },
            { value: 'google_places', label: 'Places phone' },
            { value: 'manual', label: 'Manual' },
            { value: 'csv_import', label: 'CSV import' },
          ]} />
        <label className="flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1.5 text-sm">
          <input type="checkbox" checked={emailOnly} onChange={(e) => setEmailOnly(e.target.checked)} /> Has email
        </label>
        <span className="ml-auto text-xs text-muted-foreground">{rows.length} shown</span>
      </div>

      {/* table */}
      <Card>
        {contactsQ.isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-14 text-center text-sm text-muted-foreground">
            <Contact className="h-6 w-6" />
            <p>No contacts yet.</p>
            <p className="text-xs">Hit <strong>Find contacts</strong> to pull them from company websites, or add them on a company in the pipeline.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr className="border-b border-border/60">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Role</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Phone</th>
                  <th className="px-3 py-2 font-medium">Company</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 1000).map((c) => (
                  <ContactTr key={c.id} c={c} act={act} onChanged={() => { contactsQ.refetch(); unsubQ.refetch(); }} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ContactTr({ c, act, onChanged }: { c: ContactRow; act: ReturnType<typeof useCrmAction>; onChanged: () => void }) {
  async function suppress() {
    if (!c.email) return;
    await act.mutateAsync({ action: 'suppress', email: c.email, company_id: c.company_id, reason: 'manual' });
    onChanged();
  }
  async function del() {
    await act.mutateAsync({ action: 'delete_contact', contact_id: c.id });
    onChanged();
  }
  async function makePrimary() {
    await act.mutateAsync({ action: 'update_contact', contact_id: c.id, is_primary: true });
    onChanged();
  }
  return (
    <tr className="border-b border-border/40 hover:bg-accent/40">
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{c.full_name ?? <span className="text-muted-foreground">—</span>}</span>
          {c.is_primary && <Badge variant="muted">primary</Badge>}
          {c.is_suppressed && <Badge variant="negative">unsub</Badge>}
        </div>
        {c.title && <div className="text-2xs text-muted-foreground">{c.title}</div>}
      </td>
      <td className="px-3 py-2"><Badge variant="outline" className="font-normal">{c.role_category.replace('_', ' ')}</Badge></td>
      <td className="px-3 py-2">{c.email ? <a className="text-[#51BFAA] hover:underline" href={`mailto:${c.email}`}>{c.email}</a> : <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-2 font-mono text-xs">{c.phone ?? '—'}</td>
      <td className="px-3 py-2">
        <div>{c.company_name}</div>
        <div className="text-2xs text-muted-foreground">{c.company_city} · {CATEGORY_LABEL[c.company_category as CompanyCategory]}</div>
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{SOURCE_LABEL[c.source] ?? c.source}</td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1">
          {!c.is_primary && <button title="Make primary" onClick={makePrimary} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"><Star className="h-3.5 w-3.5" /></button>}
          {c.email && !c.is_suppressed && <button title="Unsubscribe / suppress" onClick={suppress} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-[#B15599]"><BanIcon className="h-3.5 w-3.5" /></button>}
          <button title="Delete" onClick={del} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </td>
    </tr>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  apollo: 'Apollo', lusha: 'Lusha', enrichment: 'Website', google_places: 'Places', manual: 'Manual', csv_import: 'CSV',
};

function Stat({ label, value, tone, sub }: { label: string; value: number | string; tone?: 'magenta'; sub?: string }) {
  return (
    <div className="rounded-md border border-border/60 px-3 py-1.5">
      <div className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${tone === 'magenta' ? 'text-[#B15599]' : ''}`}>{value}</div>
      {sub && <div className="text-2xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
